import { DimeChip } from './DimeChip.js';
import { DimeTooltip } from './DimeTooltip.js';
import { cn } from './lib/utils.js';

export interface DimeOverflowChipItem {
  id: string;
  label: string;
  /** Screen-reader name when the visible label is not enough (e.g. a status-only indicator). */
  ariaLabel?: string;
  indicator?: 'success' | 'destructive';
}

interface DimeOverflowChipsProps {
  items: DimeOverflowChipItem[];
  // Server-side exact count, not items.length -- items may reflect only a capped fetch window.
  totalCount: number;
  maxVisible?: number;
  emptyLabel: (count: number) => string;
  /** Styles the chip row itself, so a caller whose column is right-aligned can say `justify-end`: the row is a flex container, which `text-align` cannot move. */
  className?: string;
  /** `card` is the light, bordered tooltip surface, for a list that should read as part of the page. */
  tooltipVariant?: 'default' | 'card';
}

const DEFAULT_MAX_VISIBLE = 3;
// Past this limit the tooltip shows a count instead of listing every hidden item's name.
const TOOLTIP_NAMED_LIMIT = 15;

function StatusDot({ tone }: Readonly<{ tone: 'success' | 'destructive' }>) {
  return (
    <span
      aria-hidden
      className={cn(
        'size-1.5 shrink-0 rounded-full',
        tone === 'success' ? 'bg-emerald-500' : 'bg-destructive',
      )}
    />
  );
}

export function DimeOverflowChips({
  items,
  totalCount,
  maxVisible = DEFAULT_MAX_VISIBLE,
  emptyLabel,
  className,
  tooltipVariant,
}: Readonly<DimeOverflowChipsProps>) {
  if (items.length === 0) return <>{totalCount > 0 ? emptyLabel(totalCount) : '—'}</>;

  const visible = items.slice(0, maxVisible);
  const namedHidden = items.slice(maxVisible, maxVisible + TOOLTIP_NAMED_LIMIT);
  const hiddenCount = totalCount - visible.length;
  const unnamedCount = hiddenCount - namedHidden.length;

  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {visible.map((item) => (
        <DimeChip
          key={item.id}
          variant="outline"
          className={item.indicator ? 'gap-1.5' : undefined}
        >
          {item.indicator ? <StatusDot tone={item.indicator} /> : null}
          {item.ariaLabel ? (
            <>
              <span className="sr-only">{item.ariaLabel}</span>
              <span aria-hidden>{item.label}</span>
            </>
          ) : (
            item.label
          )}
        </DimeChip>
      ))}
      {hiddenCount > 0 && (
        <DimeTooltip
          variant={tooltipVariant}
          content={
            <ul className="m-0 list-none space-y-1 p-0">
              {namedHidden.map((item) => (
                <li key={item.id} className="flex items-center gap-1.5">
                  {item.indicator ? <StatusDot tone={item.indicator} /> : null}
                  {item.label}
                </li>
              ))}
              {unnamedCount > 0 && <li className="text-muted-foreground">+{unnamedCount} more</li>}
            </ul>
          }
        >
          <DimeChip variant="secondary" tabIndex={0}>
            +{hiddenCount} more
            <span className="sr-only">
              {`: ${[
                ...namedHidden.map((item) => item.ariaLabel ?? item.label),
                ...(unnamedCount > 0 ? [`and ${unnamedCount} more`] : []),
              ].join(', ')}`}
            </span>
          </DimeChip>
        </DimeTooltip>
      )}
    </div>
  );
}
